import type { Client } from '@hey-api/client-fetch';
import { getActiveFileFolderSnapshot } from '../generated/services.gen';
import type { FileFolderResponse } from '../generated/types.gen';
import { getRegionalBaseUrl } from '../utils/get-regional-base-url';
import {
  TcFileFolderItem,
  TcFileFolderSnapshotPage,
  TcFileFolderSnapshotPageOptions,
  TcRegion,
} from './tc-tcps-api-service.models';

export async function getFilesAndFoldersSnapshotPage(
  client: Client,
  region: TcRegion,
  projectId: string,
  rootId: string,
  options: TcFileFolderSnapshotPageOptions
): Promise<TcFileFolderSnapshotPage> {
  const baseUrl = getRegionalBaseUrl(region);
  const nextRequest = options.next
    ? resolveSnapshotNextRequest(options.next, baseUrl)
    : undefined;
  const response = nextRequest
    ? await client.get<FileFolderResponse>({
        ...nextRequest,
        signal: options.signal,
      })
    : await getActiveFileFolderSnapshot({
        query: {
          projectId,
          maxItems: options.maxItems,
          objectNames: options.objectNames,
        },
        signal: options.signal,
        client,
        baseUrl,
      });

  if (response.error !== undefined) {
    throw new Error('Error while listing files and folders', {
      cause: response.error,
    });
  }
  const items = response.data?.items ?? [];
  return {
    items: items.map((item) => {
      const thumbnailUrl = item.tn ? new URL(item.tn, baseUrl).href : undefined;
      return {
        id: item.id!,
        versionId: item.vid!,
        name: item.nm!,
        type: item.tp === 'FOLDER' ? 'FOLDER' : 'FILE',
        rootElement: item.pid === rootId,
        parentId: item.pid!,
        parentType: item.ptp === 'FOLDER' ? 'FOLDER' : 'FILE',
        createdBy: item.cid,
        modifiedBy: item.mid,
        size: item.sz ?? 0,
        isDeleted: item.del ?? false,
        thumbnailUrl,
        md5: item.md5,
        createdOn: item.ct ? new Date(item.ct).toISOString() : undefined,
        modifiedOn: item.mt ? new Date(item.mt).toISOString() : undefined,
        revision: item.rv,
      } satisfies TcFileFolderItem;
    }),
    next: response.data?.next,
  };
}

function resolveSnapshotNextRequest(
  next: string,
  baseUrl: string
): Readonly<{ baseUrl: string; url: string }> {
  const url = new URL(next, `${baseUrl}/`);
  return {
    baseUrl: url.origin,
    url: `${url.pathname}${url.search}${url.hash}`,
  };
}
